/**
 * bz-dynamic-city.js — Option D dynamic URL-param city swap engine.
 *
 * Market-agnostic. Works for UK / FR / DE / US — each market embeds its own
 * cities-map JSON in a <script type="application/json"> tag and tags ONE element
 * with data-bz-cities-map-id="<element-id>" so this engine knows which to read.
 *
 * Reads ?location=<Google geo criterion ID> from window.location.search,
 * looks up the city in the inline JSON map (selector resolved via data attribute),
 * and swaps .textContent on data-bz-city-* anchors.
 *
 * Execution: runs synchronously on script load (NOT deferred). The snippet
 * intentionally injects this <script> BEFORE the heavy below-fold sections
 * so swap completes before paint settles.
 *
 * Failure modes:
 *   - No ?location= param         → no swap, fallback content stays. ✓
 *   - ?location= not in map       → no swap, fallback content stays. ✓
 *   - Map element missing         → no swap, log warning. ✓
 *   - JSON parse error            → no swap, log error. ✓
 *   - data attribute missing      → no swap, log error (snippet wiring bug). ✓
 *
 * Fixed by Codex+Gemini Plan QC 2026-05-24 (HIGH H3 / H2):
 *   - parameterized map element ID via data-bz-cities-map-id (was hardcoded
 *     "bz-uk-cities-map" — would break FR/DE/US reuse)
 *   - no hardcoded English/UK fallback strings in JS; fallback content lives
 *     entirely in the Liquid snippet (server-side rendered, market-correct)
 *
 * No external dependencies. ~60 LOC after minification.
 */
(function () {
  'use strict';

  // 1. Extract ?location= from URL.
  var params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch (e) {
    return; // ancient browser; fallback content already rendered.
  }
  var locationId = params.get('location');
  if (!locationId) return;

  // 2. Find the section root carrying the map-id pointer.
  var rootEl = document.querySelector('[data-bz-cities-map-id]');
  if (!rootEl) {
    if (window.console) {
      console.warn('[bz-dynamic-city] no [data-bz-cities-map-id] element found — snippet wiring bug');
    }
    return;
  }
  var mapElId = rootEl.getAttribute('data-bz-cities-map-id');
  if (!mapElId) return;

  // 3. Read cities map from the inline JSON script tag.
  var mapEl = document.getElementById(mapElId);
  if (!mapEl) {
    if (window.console) {
      console.warn('[bz-dynamic-city] map element #' + mapElId + ' not found');
    }
    return;
  }

  var citiesMap;
  try {
    citiesMap = JSON.parse(mapEl.textContent);
  } catch (e) {
    if (window.console) console.error('[bz-dynamic-city] JSON parse error', e);
    return;
  }

  // 3. Look up city. If not found, leave fallback content.
  var city = citiesMap[locationId];
  if (!city) {
    if (window.console) {
      console.info('[bz-dynamic-city] location ' + locationId + ' not in map; fallback retained');
    }
    return;
  }

  // 4. Swap each anchor's textContent.
  // Codex Decision QC 2026-05-25: scope querySelectorAll to rootEl (the section) so
  // multi-widget pages (or future staging-lineup-style multi-LP previews) don't
  // accidentally swap anchors in unrelated sections.
  var swaps = {
    'data-bz-city-h1-suffix':     city.h1_suffix,
    'data-bz-city-intro-prefix':  city.intro_city_prefix,
    'data-bz-city-cta-qualifier': city.cta_qualifier
  };

  Object.keys(swaps).forEach(function (attr) {
    var newText = swaps[attr];
    if (typeof newText !== 'string' || newText.length === 0) return;
    var nodes = rootEl.querySelectorAll('[' + attr + ']');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = newText;
    }
  });

  // 5. Surface a debug attribute (helps QC + can drive analytics).
  document.documentElement.setAttribute('data-bz-dynamic-city', city.name || locationId);
})();
